import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import SlideOver from '../components/SlideOver';

interface Amendment {
  id: string;
  projectId: string;
  targetArtefactId: string;
  targetArtefactVersion: string;
  title: string;
  reason: string;
  proposedChanges: string;
  impactAssessment: string | null;
  status: string;
  reviewerNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  appliedAt: string | null;
  resultingVersion: string | null;
  requestedByUserId: string | null;
  requestedBySessionId: string | null;
  createdAt: string;
  updatedAt: string;
  artefactTitle: string;
  artefactType: string;
}

interface HandedOffArtefact {
  id: string;
  title: string;
  type: string;
}

const STATUS_TABS = ['all', 'proposed', 'approved', 'applied', 'rejected'] as const;
type StatusTab = (typeof STATUS_TABS)[number];

const STATUS_BADGE: Record<string, React.CSSProperties> = {
  proposed: { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)', border: '1px solid rgba(245,158,11,0.3)' },
  approved: { background: 'rgba(124,58,237,0.10)', color: 'var(--primary)', border: '1px solid rgba(124,58,237,0.25)' },
  applied:  { background: 'rgba(16,185,129,0.12)', color: 'var(--success)', border: '1px solid rgba(16,185,129,0.3)' },
  rejected: { background: 'rgba(239,68,68,0.10)', color: 'var(--error)', border: '1px solid rgba(239,68,68,0.25)' },
};

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

interface DetailModalProps {
  amendment: Amendment;
  onClose: () => void;
  onStatusChange: (id: string, status: string, reviewerNotes?: string) => Promise<void>;
  submitting: boolean;
}

function DetailModal({ amendment, onClose, onStatusChange, submitting }: DetailModalProps) {
  const [notes, setNotes] = useState(amendment.reviewerNotes ?? '');
  const [notesError, setNotesError] = useState('');

  const handleAction = async (status: 'approved' | 'rejected' | 'applied') => {
    if (status === 'rejected' && !notes.trim()) { setNotesError('Reviewer notes are required for rejection'); return; }
    setNotesError('');
    await onStatusChange(amendment.id, status, notes.trim() || undefined);
  };

  const sectionLabel: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 6, marginTop: 20,
  };

  const renderFooter = () => {
    if (amendment.status === 'proposed') {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => void handleAction('approved')} disabled={submitting} style={actionBtnStyle('var(--success)')}>
            {submitting ? 'Saving…' : 'Approve'}
          </button>
          <button onClick={() => void handleAction('rejected')} disabled={submitting} style={actionBtnStyle('var(--error)')}>
            Reject
          </button>
          <button onClick={onClose} style={actionBtnStyle('var(--text-secondary)', true)}>Close</button>
        </div>
      );
    }
    if (amendment.status === 'approved') {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => void handleAction('applied')} disabled={submitting} style={actionBtnStyle('var(--primary)')}>
            {submitting ? 'Applying…' : 'Apply Amendment'}
          </button>
          <button onClick={onClose} style={actionBtnStyle('var(--text-secondary)', true)}>Close</button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onClose} style={actionBtnStyle('var(--text-secondary)', true)}>Close</button>
      </div>
    );
  };

  return (
    <SlideOver
      isOpen
      onClose={onClose}
      title="Amendment Details"
      subtitle={amendment.title}
      width="lg"
      footer={renderFooter()}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 9999, ...STATUS_BADGE[amendment.status] }}>
            {amendment.status.charAt(0).toUpperCase() + amendment.status.slice(1)}
          </span>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Created {formatDate(amendment.createdAt)}</span>
        </div>

        {/* Target artefact */}
        <p style={sectionLabel}>Target Artefact</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', flex: 1 }}>
            {amendment.artefactTitle}
            <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>({amendment.artefactType.replace(/_/g, ' ')}) v{amendment.targetArtefactVersion}</span>
          </span>
          <Link
            to={`/artefacts?tabs=${amendment.targetArtefactId}&active=${amendment.targetArtefactId}`}
            style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            View Artefact →
          </Link>
        </div>

        {/* Reason */}
        <p style={sectionLabel}>Reason</p>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{amendment.reason}</p>

        {/* Proposed Changes */}
        <p style={sectionLabel}>Proposed Changes</p>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', background: 'var(--surface-alt)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', border: '1px solid var(--border)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {amendment.proposedChanges}
        </div>

        {/* Impact Assessment */}
        {amendment.impactAssessment && (
          <>
            <p style={sectionLabel}>Impact Assessment</p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{amendment.impactAssessment}</p>
          </>
        )}

        {/* Reviewer Notes field — shown for proposed/approved */}
        {(amendment.status === 'proposed' || amendment.status === 'approved') && (
          <>
            <p style={{ ...sectionLabel, marginTop: 24 }}>Reviewer Notes {amendment.status === 'proposed' ? '(required for rejection)' : ''}</p>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add notes for the requester..."
              rows={3}
              style={{
                width: '100%', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
                background: 'var(--bg)', border: `1px solid ${notesError ? 'var(--error)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)', padding: '8px 10px', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              }}
            />
            {notesError && <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 4 }}>{notesError}</p>}
          </>
        )}

        {/* Reviewer Notes display — shown for reviewed */}
        {amendment.reviewerNotes && amendment.status !== 'proposed' && (
          <>
            <p style={sectionLabel}>Reviewer Notes</p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{amendment.reviewerNotes}</p>
          </>
        )}

        {/* History */}
        <p style={sectionLabel}>History</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--primary)', flexShrink: 0 }}>●</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Created — {formatDate(amendment.createdAt)}</span>
          </div>
          {amendment.reviewedAt && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: amendment.status === 'rejected' ? 'var(--error)' : 'var(--success)', flexShrink: 0 }}>●</span>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                {amendment.status === 'rejected' ? 'Rejected' : 'Approved'} — {formatDate(amendment.reviewedAt)}
              </span>
            </div>
          )}
          {amendment.appliedAt && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--success)', flexShrink: 0 }}>●</span>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                Applied — {formatDate(amendment.appliedAt)}{amendment.resultingVersion ? ` · new version v${amendment.resultingVersion} created` : ''}
              </span>
            </div>
          )}
        </div>
    </SlideOver>
  );
}

// ── Create Amendment Modal ────────────────────────────────────────────────────

interface CreateModalProps {
  projectId: string;
  handedOffArtefacts: HandedOffArtefact[];
  preselectedArtefactId?: string;
  onClose: () => void;
  onCreated: () => void;
}

function CreateModal({ projectId, handedOffArtefacts, preselectedArtefactId, onClose, onCreated }: CreateModalProps) {
  const [targetId, setTargetId] = useState(preselectedArtefactId ?? handedOffArtefacts[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [proposedChanges, setProposedChanges] = useState('');
  const [impactAssessment, setImpactAssessment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!title.trim() || !reason.trim() || !proposedChanges.trim()) { setError('Title, Reason, and Proposed Changes are required'); return; }
    setError('');
    setSubmitting(true);
    try {
      await apiFetch(`/artefacts/${targetId}/amendments`, {
        method: 'POST',
        body: JSON.stringify({ title: title.trim(), reason: reason.trim(), proposedChanges: proposedChanges.trim(), impactAssessment: impactAssessment.trim() || undefined }),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create amendment');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    padding: '8px 10px', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
    display: 'block', marginBottom: 4,
  };

  return (
    <SlideOver
      isOpen
      onClose={onClose}
      title="Create Amendment"
      subtitle="Request a change to a handed-off artefact."
      width="md"
      footer={
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => void handleSubmit()}
            disabled={submitting || handedOffArtefacts.length === 0}
            style={{
              fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '7px 16px',
              color: 'white', background: 'var(--primary)', border: 'none', borderRadius: 'var(--radius-sm)',
              cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Submitting…' : 'Submit Amendment'}
          </button>
          <button onClick={onClose} style={actionBtnStyle('var(--text-secondary)', true)}>Cancel</button>
        </div>
      }
    >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Target Artefact *</label>
            {handedOffArtefacts.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>No handed-off artefacts found. Amendments can only be created for handed-off artefacts.</p>
            ) : (
              <select value={targetId} onChange={e => setTargetId(e.target.value)} style={{ ...fieldStyle, cursor: 'pointer' }}>
                {handedOffArtefacts.map(a => (
                  <option key={a.id} value={a.id}>{a.title} ({a.type.replace(/_/g, ' ')})</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label style={labelStyle}>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief description of the amendment" style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Reason *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this amendment needed?" rows={3} style={{ ...fieldStyle, resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Proposed Changes *</label>
            <textarea value={proposedChanges} onChange={e => setProposedChanges(e.target.value)} placeholder="Describe the specific changes to be made..." rows={4} style={{ ...fieldStyle, resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Impact Assessment <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
            <textarea value={impactAssessment} onChange={e => setImpactAssessment(e.target.value)} placeholder="Which other artefacts or decisions are affected?" rows={2} style={{ ...fieldStyle, resize: 'vertical' }} />
          </div>
        </div>

        {error && <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', marginTop: 12 }}>{error}</p>}
    </SlideOver>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function actionBtnStyle(color: string, ghost = false): React.CSSProperties {
  return {
    fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
    padding: '6px 14px', background: 'transparent',
    border: `1px solid ${ghost ? 'var(--border)' : color}`,
    borderRadius: 'var(--radius-sm)', color: ghost ? 'var(--text-secondary)' : color,
    cursor: 'pointer',
  };
}

// ── Inline Reject Popover ─────────────────────────────────────────────────────

interface RejectInlineProps {
  onConfirm: (notes: string) => void;
  onCancel: () => void;
  submitting: boolean;
}

function RejectInline({ onConfirm, onCancel, submitting }: RejectInlineProps) {
  const [notes, setNotes] = useState('');
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Reviewer notes (required)..."
        rows={2}
        autoFocus
        style={{ width: '100%', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '6px 8px', resize: 'none', outline: 'none', boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button onClick={() => notes.trim() && onConfirm(notes.trim())} disabled={submitting || !notes.trim()} style={{ ...actionBtnStyle('var(--error)'), fontSize: 12, padding: '4px 10px', opacity: !notes.trim() ? 0.5 : 1 }}>
          {submitting ? 'Rejecting…' : 'Confirm Reject'}
        </button>
        <button onClick={onCancel} style={{ ...actionBtnStyle('var(--text-secondary)', true), fontSize: 12, padding: '4px 10px' }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AmendmentsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeTab = (searchParams.get('status') ?? 'all') as StatusTab;
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState('');
  const [handedOffArtefacts, setHandedOffArtefacts] = useState<HandedOffArtefact[]>([]);

  const [detailAmendment, setDetailAmendment] = useState<Amendment | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [staleArtefacts, setStaleArtefacts] = useState<{ id: string; artefactId: string; artefactTitle: string; artefactType: string; reason: string; createdAt: string }[]>([]);
  const [resolvingFlagId, setResolvingFlagId] = useState<string | null>(null);

  const fetchAmendments = () => {
    if (!projectId) return;
    setLoading(true);
    const url = activeTab === 'all' ? `/projects/${projectId}/amendments` : `/projects/${projectId}/amendments?status=${activeTab}`;
    apiFetch<Amendment[]>(url)
      .then(setAmendments)
      .catch(() => setAmendments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!projectId) return;
    apiFetch<{ name: string }>(`/projects/${projectId}`).then(p => setProjectName(p.name)).catch(() => {});
    apiFetch<{ id: string; title: string; type: string; status: string }[]>(`/artefacts?projectId=${projectId}`)
      .then(artefacts => {
        setHandedOffArtefacts(artefacts.filter(a => ['prd', 'user_stories', 'sad', 'adr'].includes(a.type)).map(a => ({ id: a.id, title: a.title, type: a.type })));
      })
      .catch(() => {});
    apiFetch<{ id: string; artefactId: string; artefactTitle: string; artefactType: string; reason: string; createdAt: string }[]>(`/projects/${projectId}/staleness`)
      .then(setStaleArtefacts)
      .catch(() => setStaleArtefacts([]));
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAmendments();
  }, [projectId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (id: string, status: string, reviewerNotes?: string) => {
    setSubmitting(true);
    try {
      const body: Record<string, string> = { status };
      if (reviewerNotes) body.reviewerNotes = reviewerNotes;
      await apiFetch(`/amendments/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
      setDetailAmendment(null);
      setRejectingId(null);
      fetchAmendments();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update amendment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolveStaleness = async (flagId: string, status: 'resolved' | 'dismissed') => {
    setResolvingFlagId(flagId);
    try {
      await apiFetch(`/staleness/${flagId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setStaleArtefacts(prev => prev.filter(f => f.id !== flagId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update staleness flag');
    } finally {
      setResolvingFlagId(null);
    }
  };

  const filtered = amendments;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {/* Header */}
        <div style={{ padding: '20px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', padding: 0 }}
            >
              ← {projectName || 'Project'}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Amendments</h1>
            <button
              onClick={() => setShowCreate(true)}
              style={{
                fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '7px 14px',
                color: 'white', background: 'var(--primary)', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              }}
            >
              + Create Amendment
            </button>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 2, marginTop: 16, borderBottom: '1px solid var(--border)' }}>
            {STATUS_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setSearchParams(tab === 'all' ? {} : { status: tab })}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
                  padding: '7px 14px', background: 'none', border: 'none', cursor: 'pointer',
                  color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
                  borderBottom: `2px solid ${activeTab === tab ? 'var(--primary)' : 'transparent'}`,
                  marginBottom: -1,
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}>
          {/* Stale artefacts section */}
          {staleArtefacts.length > 0 && (
            <div style={{ marginBottom: 24, padding: '16px 18px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-lg)', maxWidth: 760 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--warning)', marginBottom: 12 }}>
                ⚠ Stale Artefacts ({staleArtefacts.length})
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                These artefacts may be outdated due to upstream amendments. Resolve upstream artefacts first.
              </p>
              {staleArtefacts.map(sa => (
                <div key={sa.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Link
                        to={`/artefacts?tabs=${sa.artefactId}&active=${sa.artefactId}`}
                        style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        {sa.artefactTitle}
                      </Link>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', padding: '1px 6px', background: 'var(--surface-alt)', borderRadius: 4 }}>
                        {sa.artefactType.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{sa.reason}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => void handleResolveStaleness(sa.id, 'resolved')}
                      disabled={resolvingFlagId === sa.id}
                      style={{
                        fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '4px 10px',
                        background: 'transparent', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 'var(--radius-sm)',
                        color: 'var(--warning)', cursor: resolvingFlagId === sa.id ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap', opacity: resolvingFlagId === sa.id ? 0.6 : 1,
                      }}
                    >
                      {resolvingFlagId === sa.id ? 'Resolving…' : 'Mark Reviewed'}
                    </button>
                    <button
                      onClick={() => void handleResolveStaleness(sa.id, 'dismissed')}
                      disabled={resolvingFlagId === sa.id}
                      style={{
                        fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '4px 10px',
                        background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                        color: 'var(--text-secondary)', cursor: resolvingFlagId === sa.id ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap', opacity: resolvingFlagId === sa.id ? 0.6 : 1,
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse" style={{ height: 100, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)' }}>
                No {activeTab === 'all' ? '' : activeTab + ' '}amendments found
              </p>
              <button onClick={() => setShowCreate(true)} style={{ ...actionBtnStyle('var(--primary)'), marginTop: 12 }}>
                + Create Amendment
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 760 }}>
              {filtered.map(amendment => (
                <div
                  key={amendment.id}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}
                >
                  {/* Card header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{amendment.title}</h3>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 9999, whiteSpace: 'nowrap', flexShrink: 0, ...STATUS_BADGE[amendment.status] }}>
                      {amendment.status.charAt(0).toUpperCase() + amendment.status.slice(1)}
                    </span>
                  </div>

                  {/* Target */}
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    Target:{' '}
                    <Link
                      to={`/artefacts?tabs=${amendment.targetArtefactId}&active=${amendment.targetArtefactId}`}
                      style={{ color: 'var(--primary)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {amendment.artefactTitle}
                    </Link>
                    <span style={{ color: 'var(--text-secondary)' }}> ({amendment.artefactType.replace(/_/g, ' ')})</span>
                  </p>

                  {/* Reason preview */}
                  <p style={{
                    fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    lineHeight: 1.5, marginBottom: 8,
                  }}>
                    {amendment.reason}
                  </p>

                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    Created {relativeTime(amendment.createdAt)}
                  </p>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => setDetailAmendment(amendment)} style={{ ...actionBtnStyle('var(--primary)'), fontSize: 12, padding: '4px 12px' }}>
                      View Details
                    </button>
                    {amendment.status === 'proposed' && (
                      <>
                        <button
                          onClick={() => void handleStatusChange(amendment.id, 'approved')}
                          disabled={submitting}
                          style={{ ...actionBtnStyle('var(--success)'), fontSize: 12, padding: '4px 12px' }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => setRejectingId(rejectingId === amendment.id ? null : amendment.id)}
                          style={{ ...actionBtnStyle('var(--error)'), fontSize: 12, padding: '4px 12px' }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {amendment.status === 'approved' && (
                      <button
                        onClick={() => void handleStatusChange(amendment.id, 'applied')}
                        disabled={submitting}
                        style={{ ...actionBtnStyle('var(--primary)'), fontSize: 12, padding: '4px 12px' }}
                      >
                        {submitting ? 'Applying…' : 'Apply'}
                      </button>
                    )}
                  </div>

                  {/* Inline reject */}
                  {rejectingId === amendment.id && (
                    <RejectInline
                      submitting={submitting}
                      onConfirm={notes => void handleStatusChange(amendment.id, 'rejected', notes)}
                      onCancel={() => setRejectingId(null)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Modals */}
      {detailAmendment && (
        <DetailModal
          amendment={detailAmendment}
          onClose={() => setDetailAmendment(null)}
          onStatusChange={handleStatusChange}
          submitting={submitting}
        />
      )}
      {showCreate && (
        <CreateModal
          projectId={projectId!}
          handedOffArtefacts={handedOffArtefacts}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAmendments(); }}
        />
      )}
    </div>
  );
}
